import { addSeconds } from 'date-fns';
import hash from 'object-hash';
import { Response as NodeFetchResponse } from 'node-fetch';
import { configContext } from '@bymason/runtime-config';
import callEndpoint, { CallEndpointOptions } from './call-endpoint';
import ApiError, { getStatusErrorMessaging } from './api-error';
import { getHeaders } from './get-headers';

const DEFAULT_SESSION_TTL_SECS = 180; // 3-minutes

export interface CallJsonEndpointOptions extends Omit<CallEndpointOptions, 'throttleKey'> {
  errorOn404?: boolean;
  sessionTTLSeconds?: number;
  useSessionCache?: boolean;
  signal?: AbortSignal | null;
}

export interface CallResult<T = Record<string, unknown>> {
  context?: string;
  message?: string;
  data?: T;
  nextPage?: string;
  prevPage?: string;
  type?: string;
  errors?: T;
}

export interface SessionCacheModel<T> {
  data: T;
  expiresAt: number;
}

export async function callJsonEndpoint<T = void, E = void>({
  errorOn404 = true,
  headers,
  sessionTTLSeconds = DEFAULT_SESSION_TTL_SECS,
  useSessionCache,
  ...other
}: CallJsonEndpointOptions): Promise<T | void> {
  const { url, body, method, clientToken } = other;
  const signature = clientToken?.split('.')[2];
  const cacheKey = hash({ url, body, method, signature });

  if (method === 'GET' && useSessionCache && sessionStorage) {
    //
    // caller requested to use session cache, so check session
    // storage for item and return it if found and not expired
    //
    const cachedData = sessionStorage.getItem(cacheKey);

    if (cachedData) {
      const sessionData = JSON.parse(cachedData) as SessionCacheModel<T>;

      if (Date.now() < sessionData.expiresAt) {
        if (configContext !== 'production') {
          console.log('Returning cached data for GET call to:', url);
        }

        return Promise.resolve<T>(sessionData.data);
      }
    }
  }

  const hdrs = getHeaders(headers);

  hdrs.append('Content-Type', 'application/json');
  const resp = await callEndpoint({
    ...other,
    cacheKey,
    headers: hdrs,
  });

  if (!resp) {
    // no error and no response means the data call was throttled
    return Promise.resolve();
  }

  const json = await respJson(resp);

  if (!resp.ok && (resp.status !== 404 || (resp.status === 404 && errorOn404))) {
    const result = (!!json && typeof json !== 'string' ? json : {}) as CallResult<E>;

    throw new ApiError<E>(
      result.message || result.context || resp.statusText || getStatusErrorMessaging(resp.status),
      resp.status,
      result.data || result.errors,
      resp.headers.get('x-correlation-id') || undefined
    );
  }

  if (method === 'GET' && useSessionCache && sessionStorage && resp.ok && resp.status !== 404) {
    // caller requested to use session cache, so store
    // serialized result in session storage
    const sessionData: SessionCacheModel<T> = {
      data: json as T,
      expiresAt: addSeconds(new Date(), sessionTTLSeconds).getTime(),
    };
    sessionStorage.setItem(cacheKey, JSON.stringify(sessionData));
  }

  if (resp.status === 404) {
    return;
  }

  return json as T;
}

/**
 * This function is required because external APIs (e.g. Auth0) may not adhere to specifying
 * a response content-type of application/json for JSON data and in that scenario, a
 * resp.json() call fails; this helper mitigates by always getting text from the response
 * and then parsing in a defensive manner, ultimately returning text if it cannot be parsed.
 * @param resp
 * @returns
 */
async function respJson(resp: Response | NodeFetchResponse) {
  let text = '';
  try {
    text = resp.status !== 404 && resp.status !== 413 ? await resp.text() : '';

    if (text) {
      return JSON.parse(text);
    }
  } catch {
    return text;
  }
}
